import { useState, useEffect } from 'react';
import { Clock, CheckCircle, Star, Plus, Trash2 } from 'lucide-react';
import { SECTION_COLOR } from './ui';

/**
 * Provozovna → Otevírací doba a Svátky.
 *
 * Přestěhováno beze změny chování ze starého Nastavení → Směny (revize
 * Nastavení, 2026-10-12). Otevírací doba a svátky jsou vlastnost provozovny —
 * čtou je Směny (pokrytí, příplatky), Rezervace i web. Úprava tvaru na normu
 * (popisek nad hodnotou, řádky po 44 px na mobilu) je další krok.
 */
const DNY = ['Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota', 'Neděle'];

export function SvatkySprava({ isDark }) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const cardStyle = isDark
        ? { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10 }
        : { background: 'var(--surface-base)', border: '1px solid var(--border-default)', borderRadius: 10 };
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        borderRadius: 6, padding: '6px 10px', fontSize: 13,
        color: 'var(--text-primary)', outline: 'none',
    };

    const [svatky, setSvatky] = useState([]);
    const [showAdd, setShowAdd] = useState(false);
    const [newNazev, setNewNazev] = useState('');
    const [newDatum, setNewDatum] = useState('');

    const fetchSvatky = () => fetch('/api/konfigurace/svatky').then(r => r.json()).then(setSvatky);
    useEffect(() => { fetchSvatky(); }, []);

    const handleToggle = async (id, aktivni) => {
        await fetch(`/api/konfigurace/svatky/${id}`, {
            method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ aktivni: !aktivni }),
        });
        fetchSvatky();
    };

    const handleAdd = async () => {
        if (!newNazev.trim() || !newDatum) return;
        await fetch('/api/konfigurace/svatky', {
            method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ nazev: newNazev, datum: newDatum }),
        });
        setNewNazev(''); setNewDatum(''); setShowAdd(false);
        fetchSvatky();
    };

    const handleDelete = async (id) => {
        if (!confirm('Smazat tento svátek?')) return;
        await fetch(`/api/konfigurace/svatky/${id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': token } });
        fetchSvatky();
    };

    const typLabel = { fixni: 'Státní', velikonoce: 'Pohyblivý', vlastni: 'Vlastní' };
    const typColor = { fixni: '#3B82F6', velikonoce: '#8B5CF6', vlastni: '#10B981' };

    const formatDatum = (s) => {
        if (s.typ === 'fixni') return `${s.den}.${s.mesic}.`;
        if (s.typ === 'velikonoce') {
            let txt = `Velikonoce ${s.offset_dni > 0 ? '+' : ''}${s.offset_dni}d`;
            if (s.datum_letos) txt += ` (${new Date(s.datum_letos + 'T12:00:00').toLocaleDateString('cs', { day: 'numeric', month: 'short' })})`;
            return txt;
        }
        if (s.typ === 'vlastni' && s.datum) return new Date(s.datum + 'T12:00:00').toLocaleDateString('cs', { day: 'numeric', month: 'long', year: 'numeric' });
        return '';
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-3">
                <p className="text-xs md:text-sm" style={{ color: mutedColor }}>Svátky a firemní dny. Deaktivované se nezobrazí v editoru směn.</p>
                <button onClick={() => setShowAdd(!showAdd)}
                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg shrink-0 self-start"
                    style={{ background: SECTION_COLOR, color: '#fff' }}>
                    <Plus size={12} /> Vlastní svátek
                </button>
            </div>

            {showAdd && (
                <div style={{ ...cardStyle, padding: '14px 16px', marginBottom: 12 }}>
                    <div className="flex flex-col md:flex-row md:items-end gap-2.5">
                        <div className="flex-1">
                            <div className="text-[11px] font-medium mb-1" style={{ color: mutedColor }}>Název</div>
                            <input value={newNazev} onChange={e => setNewNazev(e.target.value)} placeholder="Team building..." style={{ ...inputStyle, width: '100%' }} />
                        </div>
                        <div className="md:w-40">
                            <div className="text-[11px] font-medium mb-1" style={{ color: mutedColor }}>Datum</div>
                            <input type="date" value={newDatum} onChange={e => setNewDatum(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
                        </div>
                        <div className="flex gap-2">
                            <button onClick={handleAdd} className="text-xs font-medium px-4 py-2 rounded-lg" style={{ background: SECTION_COLOR, color: '#fff' }}>Přidat</button>
                            <button onClick={() => setShowAdd(false)} className="text-xs px-3 py-2 rounded-lg" style={{ color: mutedColor }}>Zrušit</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Desktop table */}
            <div className="hidden md:block" style={{ ...cardStyle, overflow: 'hidden' }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)'}` }}>
                            {['Název', 'Typ', 'Datum', 'Aktivní', 'Akce'].map(h => (
                                <th key={h} style={{ textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {svatky.map(s => (
                            <tr key={s.id} style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}`, opacity: s.aktivni ? 1 : 0.5 }}>
                                <td style={{ padding: '8px 12px', fontWeight: 500, color: 'var(--text-primary)' }}>{s.nazev}</td>
                                <td style={{ padding: '8px 12px' }}>
                                    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: (typColor[s.typ] || '#6B7280') + '18', color: typColor[s.typ] || '#6B7280' }}>{typLabel[s.typ] || s.typ}</span>
                                </td>
                                <td style={{ padding: '8px 12px', color: mutedColor, fontVariantNumeric: 'tabular-nums' }}>{formatDatum(s)}</td>
                                <td style={{ padding: '8px 12px' }}>
                                    <label style={{ cursor: 'pointer' }}>
                                        <input type="checkbox" checked={s.aktivni} onChange={() => handleToggle(s.id, s.aktivni)} className="accent-amber-500" style={{ width: 16, height: 16 }} />
                                    </label>
                                </td>
                                <td style={{ padding: '8px 12px' }}>
                                    {s.typ === 'vlastni' && (
                                        <button onClick={() => handleDelete(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }}>
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden flex flex-col gap-2">
                {svatky.map(s => (
                    <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl" style={{ ...cardStyle, opacity: s.aktivni ? 1 : 0.5 }}>
                        <label className="shrink-0 cursor-pointer">
                            <input type="checkbox" checked={s.aktivni} onChange={() => handleToggle(s.id, s.aktivni)} className="accent-amber-500" style={{ width: 16, height: 16 }} />
                        </label>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{s.nazev}</span>
                                <span className="shrink-0" style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 99, background: (typColor[s.typ] || '#6B7280') + '18', color: typColor[s.typ] || '#6B7280' }}>{typLabel[s.typ] || s.typ}</span>
                            </div>
                            <div className="text-[11px] mt-0.5" style={{ color: mutedColor }}>{formatDatum(s)}</div>
                        </div>
                        {s.typ === 'vlastni' && (
                            <button onClick={() => handleDelete(s.id)} className="shrink-0 p-1.5" style={{ color: '#EF4444' }}>
                                <Trash2 size={14} />
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// SablonySmenSprava odstraněna — šablony se spravují v modulu Směny

export function PracovniDobaSprava({ isDark }) {
    const [dny, setDny] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const mutedColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';

    const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;

    const cardStyle = isDark
        ? { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: isMobileView ? '14px 12px' : '20px 24px' }
        : { background: 'var(--surface-base)', border: '1px solid var(--border-default)', borderRadius: 10, padding: isMobileView ? '14px 12px' : '20px 24px' };
    const inputStyle = {
        background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--surface-base)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
        borderRadius: 6, padding: '6px 10px', fontSize: 13,
        color: 'var(--text-primary)', outline: 'none',
    };

    useEffect(() => {
        fetch('/api/konfigurace/pracovni-doba').then(r => r.json()).then(data => {
            const trim = (t) => t ? t.substring(0, 5) : null; // ořez HH:mm:ss → HH:mm
            const result = [];
            for (let i = 1; i <= 7; i++) {
                const existing = data.find(d => d.den === i);
                result.push(existing
                    ? { ...existing, cas_od: trim(existing.cas_od), cas_do: trim(existing.cas_do), prijem_od: trim(existing.prijem_od), prijem_do: trim(existing.prijem_do) }
                    : { den: i, cas_od: '11:00', cas_do: '22:00', otevreno: true });
            }
            setDny(result);
        });
    }, []);

    const updateDen = (den, field, value) => {
        setDny(prev => prev.map(d => d.den === den ? { ...d, [field]: value } : d));
        setSaved(false);
    };

    const handleSave = async () => {
        setSaving(true);
        const res = await fetch('/api/konfigurace/pracovni-doba', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
            body: JSON.stringify({ dny }),
        });
        setSaving(false);
        if (res.ok) {
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        }
    };

    const DNY_KRATKE = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

    return (
        <div>
            <p className="text-xs md:text-sm mb-4" style={{ color: mutedColor }}>
                Otevírací doba a příjem objednávek pro jednotlivé dny v týdnu. Příjem bez vyplnění = jako otevírací doba; v čase konce příjmu odpálí displeje ohňostroj.
            </p>
            <div style={cardStyle}>
                <div className="flex flex-col gap-2">
                    {/* Header — hidden on mobile, shown on desktop */}
                    <div className="hidden md:grid items-center gap-3 pb-2 mb-1" style={{
                        gridTemplateColumns: '120px 40px 1fr 20px 1fr 28px 1fr 20px 1fr',
                        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}`,
                    }}>
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: mutedColor }}>Den</span>
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-center" style={{ color: mutedColor }}>Otv.</span>
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: mutedColor }}>Od</span>
                        <span />
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: mutedColor }}>Do</span>
                        <span />
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: mutedColor }}>Příjem od</span>
                        <span />
                        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: mutedColor }}>Příjem do</span>
                    </div>

                    {dny.map(d => (
                        <div key={d.den} style={{ opacity: d.otevreno ? 1 : 0.4 }}>
                            {/* Desktop row */}
                            <div className="hidden md:grid items-center gap-3" style={{
                                gridTemplateColumns: '120px 40px 1fr 20px 1fr 28px 1fr 20px 1fr',
                            }}>
                                <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                    {DNY[d.den - 1]}
                                </span>
                                <label className="flex justify-center cursor-pointer">
                                    <input type="checkbox" checked={d.otevreno}
                                        onChange={e => updateDen(d.den, 'otevreno', e.target.checked)}
                                        className="w-4 h-4 rounded cursor-pointer accent-amber-500" />
                                </label>
                                <input type="time" value={d.cas_od || ''}
                                    onChange={e => updateDen(d.den, 'cas_od', e.target.value)}
                                    disabled={!d.otevreno}
                                    style={{ ...inputStyle, opacity: d.otevreno ? 1 : 0.3 }} />
                                <span className="text-center text-xs" style={{ color: mutedColor }}>–</span>
                                <input type="time" value={d.cas_do || ''}
                                    onChange={e => updateDen(d.den, 'cas_do', e.target.value)}
                                    disabled={!d.otevreno}
                                    style={{ ...inputStyle, opacity: d.otevreno ? 1 : 0.3 }} />
                                <span />
                                <input type="time" value={d.prijem_od || ''} placeholder={d.cas_od || ''}
                                    onChange={e => updateDen(d.den, 'prijem_od', e.target.value || null)}
                                    disabled={!d.otevreno} title="Příjem objednávek od"
                                    style={{ ...inputStyle, opacity: d.otevreno ? 1 : 0.3 }} />
                                <span className="text-center text-xs" style={{ color: mutedColor }}>–</span>
                                <input type="time" value={d.prijem_do || ''} placeholder={d.cas_do || ''}
                                    onChange={e => updateDen(d.den, 'prijem_do', e.target.value || null)}
                                    disabled={!d.otevreno} title="Příjem objednávek do"
                                    style={{ ...inputStyle, opacity: d.otevreno ? 1 : 0.3 }} />
                            </div>
                            {/* Mobile row — compact */}
                            <div className="md:hidden flex items-center gap-2 py-1">
                                <label className="flex cursor-pointer shrink-0">
                                    <input type="checkbox" checked={d.otevreno}
                                        onChange={e => updateDen(d.den, 'otevreno', e.target.checked)}
                                        className="w-4 h-4 rounded cursor-pointer accent-amber-500" />
                                </label>
                                <span className="text-xs font-semibold shrink-0" style={{ color: 'var(--text-primary)', width: 24 }}>
                                    {DNY_KRATKE[d.den - 1]}
                                </span>
                                <input type="time" value={d.cas_od || ''}
                                    onChange={e => updateDen(d.den, 'cas_od', e.target.value)}
                                    disabled={!d.otevreno}
                                    style={{ ...inputStyle, flex: 1, opacity: d.otevreno ? 1 : 0.3, fontSize: 12, padding: '5px 6px' }} />
                                <span className="text-[10px]" style={{ color: mutedColor }}>–</span>
                                <input type="time" value={d.cas_do || ''}
                                    onChange={e => updateDen(d.den, 'cas_do', e.target.value)}
                                    disabled={!d.otevreno}
                                    style={{ ...inputStyle, flex: 1, opacity: d.otevreno ? 1 : 0.3, fontSize: 12, padding: '5px 6px' }} />
                            </div>
                            {/* Mobil: příjem objednávek pod otevírací dobou */}
                            <div className="md:hidden flex items-center gap-2 pb-1.5" style={{ paddingLeft: 48 }}>
                                <span className="text-[10px] shrink-0" style={{ color: mutedColor }}>příjem</span>
                                <input type="time" value={d.prijem_od || ''}
                                    onChange={e => updateDen(d.den, 'prijem_od', e.target.value || null)}
                                    disabled={!d.otevreno}
                                    style={{ ...inputStyle, flex: 1, opacity: d.otevreno ? 1 : 0.3, fontSize: 12, padding: '5px 6px' }} />
                                <span className="text-[10px]" style={{ color: mutedColor }}>–</span>
                                <input type="time" value={d.prijem_do || ''}
                                    onChange={e => updateDen(d.den, 'prijem_do', e.target.value || null)}
                                    disabled={!d.otevreno}
                                    style={{ ...inputStyle, flex: 1, opacity: d.otevreno ? 1 : 0.3, fontSize: 12, padding: '5px 6px' }} />
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-3 mt-4 pt-3" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'}` }}>
                    <button onClick={handleSave} disabled={saving}
                        className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                        style={{ background: SECTION_COLOR, color: 'white', opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'Ukládám...' : 'Uložit'}
                    </button>
                    {saved && (
                        <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#10b981' }}>
                            <CheckCircle size={14} /> Uloženo
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

